'use client';

import { apiSwagger } from '@/shared/api';
import etaLoader from '@/lib/EtaLoader';
import { Author, Category } from '@/sharedTypes/Api';
import { action, makeObservable, observable } from 'mobx';

export enum SAMPLES_MODE {
  EDIT,
  UPLOAD,
  SELECT,
  ALBUMS,
  PLAYLISTS,
  ALBUM_DETAIL,
}

export const popupCoords = {
  bottom: '50%',
  left: '50%',
  top: undefined,
  right: undefined,
};

class SamplesModel {
  sampleInfo: {
    author: Author;
    category: Category;
    title: { en: string; ru: string };
  } = {
    author: { _id: '', en: '', ru: '' },
    category: { _id: '', en: '', ru: '' },
    title: { en: '', ru: '' },
  };

  // Keep a backup copy to restore from if needed
  public _sampleInfoBackup: {
    author: Author;
    title: { en: string; ru: string };
    category: Category;
  } | null = null;

  currentMode = SAMPLES_MODE.SELECT;
  previousMode = SAMPLES_MODE.SELECT;
  selectedAlbumId: string | null = null;
  samplesHidden = true;
  ref: React.Ref<HTMLDivElement> | null = null;
  public samplesModelBtn: React.Ref<HTMLDivElement> | null = null;

  async setSampleInfo(sampleInfo: any): Promise<boolean> {
    // Guard against setting incomplete sampleInfo
    if (!sampleInfo || !sampleInfo.title || !sampleInfo.author) {
      return false;
    }
  // Fetch full author by ID
  let author: Author = { _id: '', en: '', ru: '' };
  let authorFetched = false;
  if (sampleInfo.author) {
    const authorRes = await apiSwagger.authors.authorsList();
    const authorData = await authorRes.json();
    const foundAuthor = authorData.data.find((a: Author) => a._id === sampleInfo.author);
    if (foundAuthor && foundAuthor.en) {
      author = foundAuthor;
      authorFetched = true;
    }
  }

  // Fetch full category by ID
  let category: Category = { _id: '', en: '', ru: '' };
  let categoryFetched = false;
  if (sampleInfo.category) {
    const categoryRes = await apiSwagger.categories.categoriesList();
    const categoryData = await categoryRes.json();
    const foundCategory = categoryData.data.find((c: Category) => c._id === sampleInfo.category);
    if (foundCategory && foundCategory.en) {
      category = foundCategory;
      categoryFetched = true;
    }
  }
  // Set sampleInfo with fetched objects
  this.sampleInfo = {
    title: { ...sampleInfo.title },
    author: { ...author },
    category: { ...category },
  };


    // Also save a backup copy
    this._sampleInfoBackup = {
      title: { ...sampleInfo.title },
      author: { ...author },
      category: { ...category },
    };
    
    // Return true only if we successfully fetched real data (at least 1)
    return authorFetched || categoryFetched;
  }

  restoreFromBackup() {
    if (
      this._sampleInfoBackup &&
      this._sampleInfoBackup.title.en &&
      this._sampleInfoBackup.author.en
    ) {
      this.sampleInfo = {
        title: { ...this._sampleInfoBackup.title },
        author: { ...this._sampleInfoBackup.author },
        category: { ...this._sampleInfoBackup.category },
      };
    }
  }

  constructor() {
    makeObservable(this, {
      samplesModelBtn: observable,
      sampleInfo: observable,
      currentMode: observable,
      previousMode: observable,
      selectedAlbumId: observable,
      samplesHidden: observable,
      setSampleInfo: action,
      restoreFromBackup: action,
    });

    // Initialize global instance only in browser environment
    if (typeof window !== 'undefined') {
      (window as any).samplesModel = this;
    }
  }
}

const samplesModel = new SamplesModel();
export default samplesModel;
